import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileSignature } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { DataTable } from '../../components/shared/DataTable';
import { useEnvelopes } from '../../core/hooks/useEnvelopes';
import { useAuth } from '../../core/contexts/AuthContext';
import { SignatureEnvelopeStatus } from '../../core/models/SignatureEnvelope';
import { ColumnDef } from '@tanstack/react-table';
import type { SignatureEnvelope } from '../../core/models/SignatureEnvelope';

export const EnvelopesPage: React.FC = () => {
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const { envelopes, loading, error } = useEnvelopes(organizationId);
  const [statusFilter, setStatusFilter] = useState<SignatureEnvelopeStatus | 'all'>('all');

  const getStatusColor = (status: SignatureEnvelopeStatus) => {
    switch (status) {
      case SignatureEnvelopeStatus.COMPLETED:
        return 'success';
      case SignatureEnvelopeStatus.CLIENT_SIGNED:
        return 'warning';
      case SignatureEnvelopeStatus.SENT:
        return 'secondary';
      case SignatureEnvelopeStatus.DRAFT:
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredEnvelopes = statusFilter === 'all'
    ? envelopes
    : envelopes.filter(e => e.status === statusFilter);

  const columns: ColumnDef<SignatureEnvelope>[] = [
    {
      accessorKey: 'envelopeId',
      header: 'Envelope ID',
      cell: ({ row }) => (
        <div className="font-mono text-sm">{row.original.envelopeId.substring(0, 8)}...</div>
      ),
    },
    {
      accessorKey: 'clientSigner.email',
      header: 'Project',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.clientSigner.name}</div>
          <div className="text-sm text-gray-500">{row.original.clientSigner.email}</div>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={getStatusColor(row.original.status)}>
          {row.original.status.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      accessorKey: 'sourceType',
      header: 'Source',
      cell: ({ row }) => (
        <Badge variant="outline">
          {row.original.sourceType === 'pdf_upload' ? 'PDF Upload' : 'Document Template'}
        </Badge>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => {
        const date = row.original.createdAt;
        if (date instanceof Date) {
          return date.toLocaleDateString();
        }
        return 'N/A';
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/envelopes/${row.original.envelopeId}`)}
        >
          View
        </Button>
      ),
    },
  ];

  if (loading) {
    return <LoadingState message="Loading envelopes..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load envelopes" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Signature Envelopes"
        description="Manage document signature workflows"
        icon={FileSignature}
        actions={
          <Button onClick={() => navigate('/envelopes/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Create Envelope
          </Button>
        }
      />

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('all')}
        >
          All
        </Button>
        {Object.values(SignatureEnvelopeStatus).map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(status)}
          >
            {status.replace('_', ' ')}
          </Button>
        ))}
      </div>

      {filteredEnvelopes.length === 0 ? (
        <EmptyState
          icon={FileSignature}
          title="No envelopes found"
          description={
            statusFilter === 'all'
              ? "Get started by creating a new signature envelope"
              : `No envelopes with status "${statusFilter}"`
          }
          action={
            statusFilter === 'all'
              ? {
                  label: 'Create Envelope',
                  onClick: () => navigate('/envelopes/new'),
                }
              : {
                  label: 'Show All',
                  onClick: () => setStatusFilter('all'),
                }
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={filteredEnvelopes}
          searchKey="clientSigner.email"
          searchPlaceholder="Search by client email..."
        />
      )}
    </div>
  );
};
